import type { ImgHTMLAttributes } from 'react';
import { OptimizedImage } from '@/components/OptimizedImage';
import { missingFeaturedImageLabel, publicBlogImageExists } from '@/lib/publicBlogImages';

interface BlogFeaturedImageProps {
  src: string;
  alt: string;
  frameClassName: string;
  imageClassName: string;
  width?: number;
  quality?: number;
  loading?: ImgHTMLAttributes<HTMLImageElement>['loading'];
  fetchPriority?: 'high' | 'low' | 'auto';
  sizes?: string;
  /** Related-post thumbs stay a small dashed box. The hero slot spells out the missing file. */
  compact?: boolean;
}

/**
 * Renders a blog image when the file is in public/blog/images.
 * A missing file (pending hero, for example) becomes a visible placeholder
 * instead of a broken image request.
 */
export function BlogFeaturedImage({
  src,
  alt,
  frameClassName,
  imageClassName,
  width,
  quality,
  loading,
  fetchPriority,
  sizes,
  compact = false,
}: BlogFeaturedImageProps) {
  if (!publicBlogImageExists(src)) {
    const label = missingFeaturedImageLabel(src);
    if (compact) {
      return (
        <div
          className={`${frameClassName} border border-dashed border-border bg-[#f8fafc] flex items-center justify-center p-1 text-center`}
          role="img"
          aria-label={label}
        >
          <span className="text-[10px] font-semibold leading-tight text-slate-900">PLACEHOLDER</span>
        </div>
      );
    }

    return (
      <div
        className={`${frameClassName} w-full border border-dashed border-border bg-[#f8fafc] flex items-center justify-center p-6 text-center`}
      >
        <p className="text-sm font-semibold text-slate-900">{label}</p>
      </div>
    );
  }

  return (
    <div className={`${frameClassName} overflow-hidden`}>
      <OptimizedImage
        src={src}
        alt={alt}
        width={width}
        quality={quality}
        loading={loading}
        {...(fetchPriority ? { fetchPriority } : {})}
        {...(sizes ? { sizes } : {})}
        className={imageClassName}
      />
    </div>
  );
}
